
/**
 * Competence Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class CompetenceService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'Competence listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch competence list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch competence');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Competence oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create competence');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Competence güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update competence');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Competence silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete competence');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Get personnel who have this competence
     */
    async getPersonnel(id) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/personnel`);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch personnel for competence');
        }
    }

    /**
     * Assign personnel to this competence
     */
    async assignPersonnel(id, userIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/personnel`, { userIds });
            return {
                success: true,
                data: response.data,
                message: response.message || 'Personel yetkinliğe atandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to assign personnel to competence');
        }
    }

    async unassignPersonnel(id, userId) {
        try {
            const response = await this.api.client.post(
                `${this.api.basePath}/${id}/unassign-personnel/${userId}`,
            );
            return {
                success: true,
                data: response.data,
                message: response.message || 'Personel yetkinlikten çıkarıldı',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unassign personnel from competence');
        }
    }

    async listByUser(userId) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/by-user/${userId}`);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to list competences for user');
        }
    }

    async linkForUser(userId, competenceIds) {
        try {
            const response = await this.api.client.post(
                `${this.api.basePath}/by-user/${userId}`,
                { competenceIds },
            );
            return {
                success: true,
                data: response.data,
                message: response.message || 'Yetkinlikler bağlandı',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link competences for user');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
